/**
 * Email Notification Service
 * 
 * Handles sending email notifications to users using Firebase Cloud Functions and Sendgrid.
 * This service provides comprehensive email functionality across the Gigify platform,
 * including transaction notifications, reminders, marketing communications, and user account updates.
 * Aligned with Database Bible specifications for notifications.
 */

import { db } from '../Firebase/FirebaseIndex';
import { getFunctions } from 'firebase/functions';
import { httpsCallable } from 'firebase/functions';
import { doc, addDoc, collection, serverTimestamp, updateDoc, getDoc } from 'firebase/firestore';
import { handleFirebaseError } from '../Firebase/FirebaseUtils/FirebaseErrors';
import { retryHandler } from '../Firebase/FirebaseUtils/FirebaseRetryHandler';
import debug from '../../utils/debug';
import { formatDate, parseFirebaseDate } from '../../utils/DateUtils';

// Define logo URLs for different environments
const LOGO_URLS = {
  production: 'https://firebasestorage.googleapis.com/v0/b/gigify3.firebasestorage.app/o/GIGiFY_Assets%2FGIGiFY_Logo_Vertical_with_tagline.png?alt=media&token=c4b6c39c-640d-41a0-b6f5-54039dd2f03e',
  development: '../../../assets/Logo/GIGiFY Logo Horizontal with tagline.png'
};

// Get appropriate logo URL based on environment
const getLogoUrl = () => {
  return process.env.NODE_ENV === 'production' ? LOGO_URLS.production : LOGO_URLS.development;
};

/**
 * Send an email notification to a user
 * @param {string} email - Email address of the recipient
 * @param {string} subject - Email subject line
 * @param {string} body - Email body content
 * @param {Object} data - Additional data for the email (for templates, tracking, etc.)
 * @returns {Promise<Object>} Result of the email sending operation
 */
export const sendEmailNotification = async (email, subject, body, data = {}) => {
  try {
    if (!email || !subject || !body) {
      throw new Error('Email, subject, and body are required for sending email notifications');
    }
    
    debug.firebase('EmailService', 'Sending email notification', { 
      email, 
      subject,
      category: data.category || 'general'
    });
    
    // Check user's email preferences if userId is provided
    if (data.userId) {
      const userPrefs = await getUserEmailPreferences(data.userId);
      
      // If this notification category is disabled, don't send
      if (userPrefs && !shouldSendEmailBasedOnPreferences(data.category, userPrefs)) {
        debug.log('EmailService', 'Email notification skipped due to user preferences', { 
          email, 
          category: data.category 
        });
        return { success: false, skipped: true, reason: 'user_preferences' };
      }
    }
    
    // Record email in Firestore for tracking/history
    const emailLogRef = await retryHandler.execute(async () => {
      return await addDoc(collection(db, 'emailLogs'), {
        to: email,
        subject,
        body,
        data,
        category: data.category || 'general',
        priority: data.priority || 'normal',
        status: 'pending',
        createdAt: serverTimestamp()
      });
    }, { context: 'EmailService.sendEmailNotification.createLog' });
    
    debug.firebase('EmailService', 'Email log created', { emailLogId: emailLogRef.id });
    
    // Prepare email content with proper branding and format
    const formattedBody = formatEmailBody(body, data.template || 'default', data);
    
    // Call Firebase Cloud Function to send the email
    try {
      // If running in production environment with Cloud Functions set up
      if (process.env.NODE_ENV === 'production') {
        const functions = getFunctions();
        const sendEmail = httpsCallable(functions, 'sendEmail');
        
        const result = await retryHandler.execute(async () => {
          return await sendEmail({
            to: email,
            subject,
            body: formattedBody,
            template: data.template || 'default',
            data,
            attachments: data.attachments || [],
            cc: data.cc || [],
            bcc: data.bcc || [],
            priority: data.priority || 'normal'
          });
        }, { context: 'EmailService.sendEmailNotification.sendEmail' });
        
        // Update email log with success status
        await updateDoc(doc(db, 'emailLogs', emailLogRef.id), {
          status: 'sent',
          sentAt: serverTimestamp(),
          responseData: result.data,
          trackingId: result.data.trackingId || null
        });
        
        debug.firebase('EmailService', 'Email sent successfully', { 
          email, 
          subject, 
          emailLogId: emailLogRef.id 
        });
        
        return { success: true, emailId: emailLogRef.id, trackingId: result.data.trackingId || null };
      } else {
        // For development environment, log but don't actually send
        debug.log('EmailService', 'Development mode: Email would be sent with', {
          to: email,
          subject,
          body: body.length > 100 ? `${body.substring(0, 100)}...` : body,
          data: JSON.stringify(data).length > 100 ? 'Data too large to display' : data
        });
        
        // Simulate successful sending
        await updateDoc(doc(db, 'emailLogs', emailLogRef.id), {
          status: 'sent',
          sentAt: serverTimestamp(),
          note: 'Development mode - email not actually sent'
        });
        
        return { success: true, emailId: emailLogRef.id, devMode: true };
      }
    } catch (error) {
      debug.error('EmailService', 'Error sending email', { email, subject, error });
      
      // Log failure to Firestore
      await updateDoc(doc(db, 'emailLogs', emailLogRef.id), {
        status: 'failed',
        error: error.message,
        updatedAt: serverTimestamp()
      });
      
      throw error;
    }
  } catch (error) {
    debug.error('EmailService', 'Error sending email notification', error);
    return handleFirebaseError(error, "Error sending email notification");
  }
};

/**
 * Check if an email should be sent based on user preferences
 * @param {string} category - Email category
 * @param {Object} preferences - User email preferences
 * @returns {boolean} Whether the email should be sent
 */
const shouldSendEmailBasedOnPreferences = (category, preferences) => {
  if (!preferences) return true; // Default to sending if no preferences
  if (!category) return true; // Default to sending if no category
  
  // Master email toggle - if false, don't send ANY emails
  if (preferences.emailEnabled === false) return false;
  
  // Check specific category preferences
  const categoryMapping = {
    'booking': 'bookingEmails',
    'payment': 'paymentEmails',
    'agreement': 'agreementEmails',
    'reminder': 'reminderEmails',
    'marketing': 'marketingEmails',
    'system': 'systemEmails',
    'dispute': 'disputeEmails',
    'review': 'reviewEmails'
  };
  
  const preferenceKey = categoryMapping[category.toLowerCase()];
  
  // If this category doesn't have a specific preference or is enabled, send it
  return !preferenceKey || preferences[preferenceKey] !== false;
};

/**
 * Get user's email preferences
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User's email preferences or null
 */
const getUserEmailPreferences = async (userId) => {
  try {
    debug.firebase('EmailService', 'Getting user email preferences', { userId });
    
    const userRef = doc(db, 'Users', userId);
    
    const userDoc = await retryHandler.execute(async () => {
      return await getDoc(userRef);
    }, { context: 'EmailService.getUserEmailPreferences' });
    
    if (!userDoc.exists()) {
      debug.warn('EmailService', 'User not found when checking email preferences', { userId });
      return null;
    }
    
    const userData = userDoc.data();
    debug.log('EmailService', 'Got user email preferences', { 
      userId, 
      hasPreferences: !!userData.emailPreferences 
    });
    return userData.emailPreferences || null;
  } catch (error) {
    debug.error('EmailService', 'Error getting user email preferences', error);
    return null; // Default to null on error, which will allow emails
  }
};

/**
 * Format email body with proper HTML structure, branding, etc.
 * @param {string} body - Email body content
 * @param {string} template - Template name
 * @param {Object} data - Template data
 * @returns {string} Formatted HTML email
 */
const formatEmailBody = (body, template, data = {}) => {
  // Simple formatting for plain text emails
  if (template === 'plain') {
    return body;
  }
  
  // Convert newlines to HTML breaks for better display
  const formattedBody = body.replace(/\n/g, '<br>');
  
  // Get the appropriate logo URL
  const logoUrl = getLogoUrl();
  
  // Create a basic HTML template with Gigify branding
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${data.subject || 'Gigify Notification'}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #144874;
          margin: 0;
          padding: 0;
        }
        .container {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }
        .header {
          text-align: center;
          margin-bottom: 20px;
        }
        .logo {
          max-width: 150px;
        }
        .content {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 5px;
          padding: 20px;
          margin-bottom: 20px;
        }
        .footer {
          text-align: center;
          font-size: 12px;
          color: #6c757d;
        }
        .button {
          display: inline-block;
          background-color: #01a2f1;
          color: white;
          text-decoration: none;
          padding: 10px 20px;
          border-radius: 5px;
          margin: 10px 0;
        }
        .highlight {
          background-color: #f85008;
          color: white;
          padding: 2px 5px;
          border-radius: 3px;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <img src="${logoUrl}" alt="Gigify Logo" class="logo">
        </div>
        <div class="content">
          ${formattedBody}
          
          ${data.actionUrl ? `
          <div style="text-align: center; margin-top: 20px;">
            <a href="${data.actionUrl}" class="button">${data.actionText || 'View Details'}</a>
          </div>
          ` : ''}
        </div>
        <div class="footer">
          <p>© ${new Date().getFullYear()} Gigify. All rights reserved.</p>
          <p>
            You're receiving this email because you have an account with Gigify.
            ${data.userId ? `<a href="/talent-settings/${data.userId}">Manage email preferences</a>` : ''}
          </p>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Send a templated email notification
 * @param {string} email - Email address of the recipient
 * @param {string} templateName - Name of the email template to use
 * @param {Object} templateData - Data to populate the template with
 * @param {Object} options - Additional options for the email
 * @returns {Promise<Object>} Result of the email sending operation
 */
export const sendTemplatedEmail = async (email, templateName, templateData = {}, options = {}) => {
  try {
    if (!email || !templateName) {
      throw new Error('Email and template name are required for sending templated emails');
    }
    
    debug.firebase('EmailService', 'Sending templated email', { 
      email, 
      templateName 
    });
    
    // Format dates in template data
    const formattedTemplateData = { ...templateData };
    if (formattedTemplateData.eventDate) {
      formattedTemplateData.formattedDate = formatDate(formattedTemplateData.eventDate);
    }
    
    // Get default subject and body based on template
    const { subject, body, category } = getEmailTemplate(templateName, formattedTemplateData);
    
    // Override subject and body if provided in options
    const finalSubject = options.subject || subject;
    const finalBody = options.body || body;
    
    // Send the email
    return await sendEmailNotification(email, finalSubject, finalBody, {
      ...options,
      template: templateName,
      templateData: formattedTemplateData,
      category: options.category || category || 'general'
    });
  } catch (error) {
    debug.error('EmailService', 'Error sending templated email', error);
    return handleFirebaseError(error, "Error sending templated email");
  }
};

/**
 * Get an email template with populated data
 * @param {string} templateName - Name of the email template
 * @param {Object} data - Data to populate the template with
 * @returns {Object} The subject and body for the email
 */
const getEmailTemplate = (templateName, data = {}) => {
  // Define templates for different email types
  const templates = {
    // === USER ACCOUNT TEMPLATES ===
    
    // Welcome email template
    welcome: {
      subject: 'Howzit! Welcome to Gigify!',
      body: `Hi ${data.firstName || 'there'},
      
Welcome to Gigify, South Africa's premier talent booking platform! We're stoked to have you join our vibrant community of talented performers and awesome event organisers.

${data.userType === 'Talent' 
  ? 'Your talent profile has been created. Complete your profile to start receiving booking requests and get the gigs rolling in!' 
  : 'Your organiser account has been created. You can now start browsing and booking top-notch talent for your next lekker event!'}

If you have any questions, please don't hesitate to give our friendly support team a shout.

Cheers!
The Gigify Team`,
      category: 'system'
    },
    
    // Email verification template
    verify_email: {
      subject: 'Please Verify Your Gigify Email Address',
      body: `Hi ${data.firstName || 'there'},
      
Thanks for registering with Gigify! Please verify your email address by clicking the link below:

${data.verificationLink || '[Verification link not provided]'}

This link will expire in 24 hours, so hop to it!

Cheers,
The Gigify Team`,
      category: 'system'
    },
    
    // Profile completion reminder template
    profile_completion: {
      subject: 'Complete Your Gigify Profile',
      body: `Hi ${data.firstName || 'there'},
      
Your Gigify profile is ${data.completionPercentage || 0}% complete. Finish setting up your profile to ${data.userType === 'Talent' ? 'increase your chances of being booked – more visibility, more gigs!' : 'have a better experience finding amazing talent for your events!'}

Click the link below to complete your profile:
${data.userType === 'Talent' 
  ? `/talent-dashboard/${data.userId}` 
  : `/organiser-dashboard/${data.userId}`}

Cheers,
The Gigify Team`,
      category: 'system'
    },
    
    // Password reset template
    password_reset: {
      subject: 'Reset Your Gigify Password',
      body: `Hi there,
      
We received a request to reset your password for your Gigify account. Click the link below to reset your password:

${data.resetLink || '[Reset link not provided]'}

If you didn't request this, you can safely ignore this email.

Cheers,
The Gigify Team`,
      category: 'system'
    },
    
    // Account update notification
    account_update: {
      subject: 'Your Gigify Account Has Been Updated',
      body: `Hi ${data.firstName || 'there'},
      
This is to confirm that your Gigify account has been updated.

${data.updateDetails ? `Details: ${data.updateDetails}` : ''}

If you did not make these changes, please contact our support team right away.

Regards,
The Gigify Team`,
      category: 'system'
    },
    
    // === BOOKING NOTIFICATION TEMPLATES ===
    
    // New booking request template
    booking_request: {
      subject: `Exciting News! New Booking Request: ${data.eventName || 'Event'}`,
      body: `Hi ${data.talentName || 'there'},
      
Yebo! You've received a new booking request from ${data.organiserName || 'an organiser'} for "${data.eventName || 'an event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'}.

Event Details:
- Event: ${data.eventName || 'N/A'}
- Date: ${data.formattedDate || formatDate(data.eventDate) || 'N/A'}
- Time: ${data.startTime || 'N/A'} to ${data.endTime || 'N/A'}
- Venue: ${data.venue || 'N/A'}
- Location: ${data.venueAddress || 'N/A'}

Please log in to your Gigify dashboard to accept or decline this booking request. This could be the gig you've been waiting for!

Cheers,
The Gigify Team`,
      category: 'booking'
    },
    
    // Booking accepted template
    booking_accepted: {
      subject: `Fantastic News! Booking Accepted: ${data.eventName || 'Your Event'}`,
      body: `Hi ${data.organiserName || 'there'},
      
Woohoo! ${data.talentName || 'The talent'} has accepted your booking request for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'}.

The next step is to review and sign the performance agreement, which will be generated automatically.

Event Details:
- Event: ${data.eventName || 'N/A'}
- Date: ${data.formattedDate || formatDate(data.eventDate) || 'N/A'}
- Time: ${data.startTime || 'N/A'} to ${data.endTime || 'N/A'}
- Venue: ${data.venue || 'N/A'}
- Location: ${data.venueAddress || 'N/A'}

Regards,
The Gigify Team`,
      category: 'booking'
    },
    
    // Booking declined template
    booking_declined: {
      subject: `Booking Declined: ${data.eventName || 'Your Event'}`,
      body: `Hi ${data.organiserName || 'there'},
      
We're sorry to inform you that ${data.talentName || 'the talent'} has declined your booking request for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'}.

${data.reason ? `Reason provided: "${data.reason}"` : 'No reason was provided.'}

Don't worry! You can browse other talent on our platform and submit a new booking request. There are lots of fish in the sea!

Regards,
The Gigify Team`,
      category: 'booking'
    },
    
    // Booking confirmation template
    booking_confirmation: {
      subject: `It's Official! Booking Confirmed: ${data.eventName || 'Your Event'}`,
      body: `Hi ${data.recipientName || 'there'},
      
Your booking for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} has been confirmed! Everything is set and good to go.

${data.userType === 'Talent' 
  ? 'The event organiser has confirmed your booking. You can view the booking details on your dashboard.' 
  : 'Your talent booking has been confirmed. You can view the booking details on your dashboard.'}

Booking details:
- Event: ${data.eventName || 'N/A'}
- Date: ${data.formattedDate || formatDate(data.eventDate) || 'N/A'}
- Time: ${data.startTime || 'N/A'} to ${data.endTime || 'N/A'}
- Venue: ${data.venue || 'N/A'}
- Location: ${data.venueAddress || 'N/A'}
- Talent: ${data.talentName || 'N/A'}

If you have any questions, please don't hesitate to contact our support team.

Regards,
The Gigify Team`,
      category: 'booking'
    },
    
    // Booking cancelled template
    booking_cancelled: {
      subject: `Booking Cancelled: ${data.eventName || 'Your Event'}`,
      body: `Hi ${data.recipientName || 'there'},
      
This is to inform you that the booking for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} has been cancelled.

${data.reason ? `Reason for cancellation: "${data.reason}"` : 'No reason was provided for the cancellation.'}

${data.penaltyAmount ? `Cancellation penalty: R${data.penaltyAmount.toFixed(2)}` : ''}
${data.refundAmount ? `Refund amount: R${data.refundAmount.toFixed(2)}` : ''}

If you have any questions, please contact our support team.

Regards,
The Gigify Team`,
      category: 'booking'
    },
    
    // === PAYMENT TEMPLATES ===
    
    // Payment required template
    payment_required: {
      subject: `Payment Required: ${data.eventName || 'Your Event'}`,
      body: `Hi ${data.organiserName || 'there'},
      
Thanks for booking ${data.talentName || 'talent'} for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'}.

Your booking agreement has been signed, and payment is now required to confirm the booking.

Payment Details:
- Total Amount: R${data.amount ? data.amount.toFixed(2) : 'N/A'}
- Due Date: ${data.dueDate || 'As soon as possible'}

Please log in to your Gigify dashboard to complete the payment securely through our escrow system.

${data.bookingId ? `Or click here to go directly to payment: /payment/${data.bookingId}` : ''}

Please note: Your booking is not officially confirmed until payment is received, and no advertising of the talent's performance is permitted until payment is confirmed.

Regards,
The Gigify Team`,
      category: 'payment'
    },
    
    // Payment reminder template
    payment_reminder: {
      subject: `Gentle Reminder: Payment Due for ${data.eventName || 'Your Event'}`,
      body: `Hi ${data.organiserName || 'there'},
      
Just a friendly reminder that payment is due for your booking of ${data.talentName || 'talent'} for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'}.

Payment Details:
- Total Amount: R${data.amount ? data.amount.toFixed(2) : 'N/A'}
- Due Date: ${data.dueDate || 'As soon as possible'}

${data.daysLate ? `Your payment is now ${data.daysLate} day(s) late.` : ''}

Please log in to your Gigify dashboard to complete the payment securely through our escrow system.

${data.bookingId ? `Or click here to go directly to payment: /payment/${data.bookingId}` : ''}

Please note: Your booking will be automatically cancelled if payment is not received within 48 hours.

Regards,
The Gigify Team`,
     category: 'payment'
   },
   
   // Payment receipt template
   payment_receipt: {
     subject: `Payment Received for ${data.eventName || 'Your Event'}`,
     body: `Hi ${data.organiserName || 'there'},
     
Shot for your payment for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'}!

Payment Details:
- Transaction ID: ${data.transactionId || 'N/A'}
- Amount Paid: R${data.amount ? data.amount.toFixed(2) : 'N/A'}
- Payment Date: ${data.paymentDate || 'N/A'}
- Payment Method: ${data.paymentMethod || 'N/A'}

Your booking of ${data.talentName || 'talent'} is now confirmed. You can view the booking details on your dashboard.

You can download a receipt of this transaction from your Gigify dashboard.

Regards,
The Gigify Team`,
     category: 'payment'
   },
   
   // Payment in escrow notification
   payment_in_escrow: {
     subject: `Payment Secured in Escrow: ${data.eventName || 'Your Event'}`,
     body: `Hi ${data.talentName || 'there'},
     
Good news! The payment for "${data.eventName || 'the event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} has been secured in escrow.

Payment Details:
- Amount: R${data.amount ? data.amount.toFixed(2) : 'N/A'}
- Release Date: Funds will be released to you 3 days after the event's completion

Your booking is now officially confirmed. Please ensure you fulfill all requirements as specified in the performance agreement.

Regards,
The Gigify Team`,
     category: 'payment'
   },
   
   // Logistics payment release notification
   logistics_payment_release: {
     subject: `Logistics Payment Released: ${data.eventName || 'Your Event'}`,
     body: `Hi ${data.talentName || 'there'},
     
Great news! The logistics payment for "${data.eventName || 'the event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} has been released to your account.

Logistics Payment Details:
- Amount: R${data.amount ? data.amount.toFixed(2) : 'N/A'}
- Release Date: ${data.releaseDate || 'Today'}

This payment is specifically for your travel arrangements. The performance fee will be released after the event's completion.

Regards,
The Gigify Team`,
     category: 'payment'
   },
   
   // Payment released notification
   payment_released: {
     subject: `Payment Released: ${data.eventName || 'Your Event'}`,
     body: `Hi ${data.talentName || 'there'},
     
Awesome news! The payment for "${data.eventName || 'the event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} has been released to your account.

Payment Details:
- Amount: R${data.amount ? data.amount.toFixed(2) : 'N/A'}
- Release Date: ${data.releaseDate || 'Today'}

Thank you for using Gigify for your performance booking.

Regards,
The Gigify Team`,
     category: 'payment'
   },
   
   // === AGREEMENT TEMPLATES ===
   
   // Agreement ready for signature template
   agreement_ready: {
     subject: `URGENT: Performance Agreement Ready for ${data.eventName || 'Your Event'} - 48 Hour Deadline`,
     body: `Hi ${data.recipientName || 'there'},
     
The performance agreement for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} is now ready for your signature.

⚠️ IMPORTANT: This agreement must be signed within 2 days (48 hours), or it will expire and need to be regenerated. The deadline is ${data.deadline || 'in 2 days from now'}.

The agreement includes:
- Event details
- Performance specifications
${data.hasTechnicalRider ? '- Technical rider requirements' : ''}
${data.hasLogisticsRider ? '- Logistics and hospitality rider requirements' : ''}
- Payment terms and conditions

Please log in to your Gigify dashboard to review and sign the agreement.

${data.bookingId ? `Click here to go directly to the signing page: /signature-agreement/${data.bookingId}` : ''}

Regards,
The Gigify Team`,
     category: 'agreement'
   },
   
   // Agreement reminder template
   agreement_reminder: {
     subject: `URGENT: Sign Performance Agreement for ${data.eventName || 'Your Event'} - Time is Running Out!`,
     body: `Hi ${data.recipientName || 'there'},
     
This is an urgent reminder that the performance agreement for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} is waiting for your signature.

${data.expiresIn ? `The agreement will expire in ${data.expiresIn}.` : ''}
${data.hoursRemaining && data.hoursRemaining <= 24 ? `⚠️ You have less than ${data.hoursRemaining} hours remaining to sign this agreement.` : ''}

Please note that unsigned agreements automatically expire after 2 days (48 hours). If the agreement expires, the booking request will need to be submitted again from scratch.

Please log in to your Gigify dashboard to review and sign the agreement.

${data.bookingId ? `Click here to go directly to the signing page: /signature-agreement/${data.bookingId}` : ''}

Regards,
The Gigify Team`,
     category: 'agreement'
   },
   
   // Agreement signed notification
   agreement_signed: {
    subject: `Agreement Signed: ${data.eventName || 'Your Event'}`,
    body: `Hi ${data.recipientName || 'there'},
    
Great news! The performance agreement for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} has been signed by all parties.

${data.userType === 'Organiser' ? 'The next step is to complete the payment to confirm the booking.' : 'The booking will be confirmed once the organiser completes the payment.'}

You can view and download the signed agreement from your Gigify dashboard.

${data.userType === 'Organiser' && data.bookingId ? `Click here to make payment: /payment/${data.bookingId}` : ''}

Regards,
The Gigify Team`,
    category: 'agreement'
  },
  
  // Agreement rejected notification
  agreement_rejected: {
    subject: `Agreement Rejected: ${data.eventName || 'Your Event'}`,
    body: `Hi ${data.recipientName || 'there'},
    
The performance agreement for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} has been rejected.

Reason for rejection: ${data.reason || 'No reason provided'}

Please contact the other party directly to resolve any issues, or you can create a new booking request.

Regards,
The Gigify Team`,
    category: 'agreement'
  },
  
  // Agreement expired notification
  agreement_expired: {
    subject: `Agreement Expired: ${data.eventName || 'Your Event'}`,
    body: `Hi ${data.recipientName || 'there'},
    
We regret to inform you that the performance agreement for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} has expired.

The agreement was not signed within the required 2-day (48-hour) timeframe and has automatically expired.

To proceed with this booking, the organiser must resubmit a booking request form to start the booking process again. This is the only way to regenerate the agreement.

${data.userType === 'Talent' 
 ? 'Please inform the organiser if you are still interested in performing at this event.' 
 : 'You will need to submit a new booking request if you still wish to book the talent for this event.'}

Regards,
The Gigify Team`,
    category: 'agreement'
  },
  
  // === EVENT REMINDER TEMPLATES ===
  
  // Event reminder for talent
  talent_event_reminder: {
    subject: `Upcoming Performance: ${data.eventName || 'Event'} (${data.timeFrame || 'Soon'})`,
    body: `Hi ${data.talentName || 'there'},
    
Heads up! Just a reminder about your upcoming performance at "${data.eventName || 'the event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'}.

Event Details:
- Date: ${data.formattedDate || formatDate(data.eventDate) || 'N/A'}
- Time: ${data.startTime || 'N/A'} to ${data.endTime || 'N/A'}
- Venue: ${data.venue || 'N/A'}
- Address: ${data.venueAddress || 'N/A'}
${data.soundCheckTime ? `- Sound Check Time: ${data.soundCheckTime}` : ''}
${data.arrivalTime ? `- Arrival Time: ${data.arrivalTime}` : ''}

${data.timeFrame === '1 day' || data.hoursUntilEvent < 24 ? 'Time to get ready! Please ensure all your equipment is prepared and travel arrangements are confirmed.' : ''}

${data.hasRiderRequirements ? 'Remember to review the technical and logistics rider requirements in your dashboard.' : ''}

${data.userId && data.bookingId ? `View booking details: /talent-confirmed-bookings/${data.userId}?focus=${data.bookingId}` : ''}

Regards,
The Gigify Team`,
    category: 'reminder'
  },
  
  // Event reminder for organiser
  organiser_event_reminder: {
    subject: `Upcoming Event: ${data.eventName || 'Your Event'} (${data.timeFrame || 'Soon'})`,
    body: `Hi ${data.organiserName || 'there'},
    
Quick reminder about your upcoming event "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'}.

Event Details:
- Date: ${data.formattedDate || formatDate(data.eventDate) || 'N/A'}
- Time: ${data.startTime || 'N/A'} to ${data.endTime || 'N/A'}
- Venue: ${data.venue || 'N/A'}
- Address: ${data.venueAddress || 'N/A'}
- Talent: ${data.talentName || 'N/A'}
${data.soundCheckTime ? `- Sound Check Time: ${data.soundCheckTime}` : ''}

${data.timeFrame === '1 day' || data.hoursUntilEvent < 24 ? 'It\'s almost showtime! Please ensure the venue is prepared according to the technical rider requirements and all logistics arrangements are confirmed.' : ''}

${data.bookingId ? `View booking details: /booking-details/${data.bookingId}` : ''}

Regards,
The Gigify Team`,
    category: 'reminder'
  },
  
  // Post-event review request
  review_request: {
    subject: `Share Your Experience: ${data.eventName || 'Your Event'}`,
    body: `Hi ${data.recipientName || 'there'},
    
Thank you for using Gigify for "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'}.

How did it go? Your feedback is gold to our community!

Please take a quick moment to leave a review for ${data.userType === 'Talent' ? data.organiserName || 'the organiser' : data.talentName || 'the talent'}.

${data.userType === 'Talent' ? 'Your rating helps other talent know what to expect when working with this organiser.' : 'Your rating helps other organisers make informed decisions when booking talent.'}

${data.bookingId ? `Leave your review here: /booking-details/${data.bookingId}?review=true` : 'You can leave your review through your Gigify dashboard.'}

Regards,
The Gigify Team`,
    category: 'review'
  },
  
  // === DISPUTE TEMPLATES ===
  dispute_initiated: {
    subject: `Dispute Initiated: ${data.eventName || 'Your Event'}`,
    body: `Hi ${data.recipientName || 'there'},
    
A dispute has been initiated regarding the booking of "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'}.

Dispute Details:
- Initiated By: ${data.disputeInitiator || 'N/A'}
- Reason: ${data.disputeReason || 'No reason provided'}
- Evidence Provided: ${data.hasEvidence ? 'Yes' : 'No'}

${data.bookingId ? `Please log in to view the dispute details: /booking-details/${data.bookingId}?tab=dispute` : 'Please log in to your Gigify dashboard to view the full dispute details and respond.'}

Regards,
The Gigify Team`,
    category: 'dispute'
  },

  // Dispute resolution notification
  dispute_resolution: {
    subject: `Dispute Resolution: ${data.eventName || 'Your Event'}`,
    body: `Hi ${data.recipientName || 'there'},
    
The dispute regarding "${data.eventName || 'your event'}" on ${data.formattedDate || formatDate(data.eventDate) || '[date not provided]'} has been resolved.

Resolution Details:
- Resolution Status: ${data.resolutionStatus || 'N/A'}
- Resolution Summary: ${data.resolutionSummary || 'No details provided'}

${data.financialImpact ? `Financial Impact: R${data.financialImpact.toFixed(2)}` : ''}

${data.bookingId ? `View the full details here: /booking-details/${data.bookingId}?tab=dispute` : 'Please log in to your Gigify dashboard for more information.'}

Regards,
The Gigify Team`,
    category: 'dispute'
  },

  // === SUPPORT & COMMUNICATION TEMPLATES ===

  // Support ticket created
  support_ticket_created: {
    subject: `Support Ticket #${data.ticketId}: ${data.subject || 'New Support Request'}`,
    body: `Hi ${data.userName || 'there'},
    
Thank you for contacting Gigify Support. We have received your support request and assigned it a ticket number.

Ticket Details:
- Ticket Number: #${data.ticketId}
- Subject: ${data.subject || 'N/A'}
- Category: ${data.category || 'General Inquiry'}
- Submitted On: ${data.submissionDate || 'Just now'}

Our support team will review your ticket and respond as soon as possible. You can track the status of your ticket through our support portal.

Regards,
Gigify Support Team`,
    category: 'support'
  },

  // Support ticket update
  support_ticket_update: {
    subject: `Support Ticket #${data.ticketId}: Update Received`,
    body: `Hi ${data.userName || 'there'},
    
There's an update on your support ticket #${data.ticketId} regarding "${data.subject || 'your inquiry'}".

Update Details:
- Status: ${data.status || 'N/A'}
- Response: ${data.responseSnippet || 'No detailed response yet'}

${data.requiresAction ? 'Action may be required from your side.' : ''}

Please log in to the support portal to view the full update.

Regards,
Gigify Support Team`,
    category: 'support'
  },

  // Default template for unknown template names
  default: {
    subject: data.subject || 'Gigify Notification',
    body: data.body || 'This is a notification from Gigify.',
    category: 'general'
  }
};

// Return the requested template, or the default template if not found
return templates[templateName] || templates.default;
};

// Class property for additional templates
let additionalTemplates = {};

/**
* Method to add custom templates dynamically
* @param {string} name - Template name
* @param {Object} template - Template object with subject, body, and optional category
*/
export const addCustomTemplate = (name, template) => {
debug.log('EmailService', 'Adding custom template', { templateName: name });
additionalTemplates[name] = template;
};

/**
* Send a custom email with full flexibility
* @param {string} email - Recipient email
* @param {Object} options - Comprehensive email options
* @returns {Promise<Object>} Result of the email sending operation
*/
export const sendCustomEmail = async (email, options = {}) => {
try {
  debug.firebase('EmailService', 'Sending custom email', { email });
  
  const {
    subject,
    body,
    html,
    template,
    templateData = {},
    attachments = [],
    cc = [],
    bcc = [],
    replyTo,
    priority = 'normal',
    category = 'general',
    userId
  } = options;
  
  // If a template is specified, use sendTemplatedEmail
  if (template) {
    return await sendTemplatedEmail(email, template, templateData, {
      attachments,
      cc,
      bcc,
      replyTo,
      priority,
      category,
      userId
    });
  }
  
  // If no template, use raw email sending
  return await sendEmailNotification(email, subject, body, {
    attachments,
    cc,
    bcc,
    replyTo,
    priority,
    category,
    userId,
    html
  });
} catch (error) {
  debug.error('EmailService', 'Error sending custom email', error);
  return handleFirebaseError(error, "Error sending custom email");
}
};

/**
* Bulk email sending utility
* @param {Array} recipients - Array of recipient email addresses or objects
* @param {Object} options - Email sending options
* @returns {Promise<Array>} Results of email sending for each recipient
*/
export const sendBulkEmail = async (recipients, options = {}) => {
try {
  debug.firebase('EmailService', 'Sending bulk email', { 
    recipientCount: recipients.length 
  });
  
  const { 
    template, 
    templateData = {}, 
    batchSize = 50, 
    delayBetweenBatches = 5000 // 5 seconds between batches
  } = options;
  
  const results = [];
  
  // Normalize recipients to array of objects
  const normalizedRecipients = recipients.map(recipient => 
    typeof recipient === 'string' 
      ? { email: recipient } 
      : recipient
  );
  
  // Process in batches to avoid overwhelming email service
  for (let i = 0; i < normalizedRecipients.length; i += batchSize) {
    const batch = normalizedRecipients.slice(i, i + batchSize);
    
    debug.log('EmailService', `Processing batch ${Math.floor(i/batchSize) + 1}`, { 
      batchSize: batch.length 
    });
    
    const batchPromises = batch.map(async (recipient) => {
      try {
        // Merge recipient-specific data with template data
        const mergedTemplateData = {
          ...templateData,
          ...recipient.personalData
        };
        
        return await sendCustomEmail(recipient.email, {
          ...options,
          templateData: mergedTemplateData
        });
      } catch (error) {
        return { 
          success: false, 
          email: recipient.email, 
          error: error.message 
        };
      }
    });
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
    
    // Add delay between batches to prevent rate limiting
    if (i + batchSize < normalizedRecipients.length) {
      await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
    }
  }
  
  const successCount = results.filter(r => r.success).length;
  debug.firebase('EmailService', 'Bulk email completed', { 
    total: results.length,
    successful: successCount,
    failed: results.length - successCount
  });
  
  return results;
} catch (error) {
  debug.error('EmailService', 'Error sending bulk email', error);
  return handleFirebaseError(error, "Error sending bulk email");
}
};

/**
* Email preview utility for testing templates
* @param {string} templateName - Name of the template to preview
* @param {Object} data - Sample data for the template
* @returns {Object} Preview of the email template
*/
export const previewEmailTemplate = (templateName, data = {}) => {
debug.log('EmailService', 'Previewing email template', { templateName });

const template = getEmailTemplate(templateName, data);

return {
  subject: template.subject,
  body: template.body,
  htmlBody: formatEmailBody(template.body, 'default', {
    subject: template.subject,
    ...data
  }),
  category: template.category
};
};

/**
* Get all available email templates
* @returns {Object} Object with all template names and categories
*/
export const getAvailableTemplates = () => {
debug.log('EmailService', 'Getting available email templates');

// Get a sample template to extract all template names
const sampleData = {};
const templates = {};

// Loop through all templates to get their categories
for (const templateName in getEmailTemplate('default', sampleData)) {
  if (templateName !== 'default') {
    const template = getEmailTemplate(templateName, sampleData);
    templates[templateName] = template.category || 'general';
  }
}

// Add any custom templates
for (const templateName in additionalTemplates) {
  templates[templateName] = additionalTemplates[templateName].category || 'custom';
}

return templates;
};

// Export all email service functions
export default {
sendEmailNotification,
sendTemplatedEmail,
sendCustomEmail,
sendBulkEmail,
previewEmailTemplate,
addCustomTemplate,
getAvailableTemplates
};